import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import phaseBillingService from '../../services/phaseBillingService';
import { toast } from 'sonner';
import discountService from '../../services/discountService';
import jsPDF from 'jspdf';

/**
 * Page de suivi détaillé des factures par dentiste
 * Affiche : Cas → Phases → Batches → Factures
 */
const DentistInvoicesTracking = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [applyingDiscount, setApplyingDiscount] = useState({});
  
  const [loading, setLoading] = useState(true);
  const [casesData, setCasesData] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    phaseStatus: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedDentist, setSelectedDentist] = useState(null);
  const [dentists, setDentists] = useState([]);

  // Discount modal states
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [selectedInvoiceForDiscount, setSelectedInvoiceForDiscount] = useState(null);
  const [selectedCaseForDiscount, setSelectedCaseForDiscount] = useState(null);
  const [availableDiscountRules, setAvailableDiscountRules] = useState([]);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Facturation', path: '/billing-overview', icon: 'CreditCard' },
    { label: 'Suivi par cas et phases', path: '/dentist-invoices-tracking', icon: 'FileText' }
  ];

  // ==========================================
  // DISCOUNT SELECTION MODAL COMPONENT
  // ==========================================

  const DiscountSelectionModal = ({ 
    isOpen, 
    onClose, 
    invoice, 
    caseItem, 
    availableRules,
    onApplyDiscount,
    isApplying 
  }) => {
    const [selectedRuleId, setSelectedRuleId] = useState(null);

    if (!isOpen) return null;

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: caseItem?.currencyCode || 'EUR',
      }).format(value ?? 0);
    };

    const calculateDiscountAmount = (rule) => {
      const subtotal = parseFloat(invoice.subtotal || invoice.total_amount);
      return (subtotal * parseFloat(rule.discount_percentage)) / 100;
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Sélectionner une remise
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Facture: {invoice.invoice_number}
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isApplying}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {availableRules.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Tag" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune remise disponible pour cette facture</p>
              </div>
            ) : (
              <div className="space-y-3">
                {availableRules.map((rule) => {
                  const discountAmount = calculateDiscountAmount(rule);
                  const isSelected = selectedRuleId === rule.id;

                  return (
                    <div
                      key={rule.id}
                      onClick={() => !isApplying && setSelectedRuleId(rule.id)}
                      className={`
                        border rounded-lg p-4 cursor-pointer transition-all
                        ${isSelected 
                          ? 'border-primary bg-primary/5 shadow-md' 
                          : 'border-border hover:border-primary/50 hover:bg-muted/30'
                        }
                        ${isApplying ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`
                            w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'}
                          `}>
                            {isSelected && (
                              <Icon name="Check" size={14} className="text-white" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">
                              {rule.rule_name}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {rule.rule_code}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-success">
                            {rule.discount_percentage}%
                          </p>
                          <p className="text-xs text-muted-foreground">
                            de remise
                          </p>
                        </div>
                      </div>

                      {rule.description && (
                        <p className="text-sm text-muted-foreground mb-2 ml-8">
                          {rule.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between ml-8">
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className={`
                            px-2 py-1 rounded-full
                            ${rule.rule_type === 'volume' ? 'bg-blue-500/10 text-blue-500' :
                              rule.rule_type === 'loyalty' ? 'bg-purple-500/10 text-purple-500' :
                              rule.rule_type === 'early_payment' ? 'bg-green-500/10 text-green-500' :
                              'bg-orange-500/10 text-orange-500'
                            }
                          `}>
                            {rule.rule_type === 'volume' ? 'Volume' :
                             rule.rule_type === 'loyalty' ? 'Fidélité' :
                             rule.rule_type === 'early_payment' ? 'Paiement anticipé' :
                             rule.rule_type || 'Autre'}
                          </span>
                          {rule.min_order_quantity && (
                            <span>Min: {rule.min_order_quantity} aligneurs</span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-success">
                            -{formatCurrency(discountAmount)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isApplying}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={() => {
                if (selectedRuleId) {
                  onApplyDiscount(selectedRuleId);
                }
              }}
              disabled={!selectedRuleId || isApplying}
              loading={isApplying}
              iconName="Tag"
              iconPosition="left"
            >
              Appliquer la remise
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // USE EFFECTS
  // ==========================================

  useEffect(() => {
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      if (isAdmin) {
        fetchDentists();
      } else {
        fetchCasesData(currentUser.id);
      }
    }
  }, [currentUser, isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedDentist) {
      fetchCasesData(selectedDentist);
    }
  }, [selectedDentist]);

  useEffect(() => {
    filterCases();
  }, [filters, casesData]);

  // ==========================================
  // HANDLERS
  // ==========================================
const handleDownloadInvoicePdf = (invoice, caseItem, phase) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const currencyCode = caseItem?.currencyCode || 'EUR';
  const W = 210; // A4 width mm
  const margin = 14;
  const contentW = W - margin * 2;

 const fmt = (val) => {
  const formatted = new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: currencyCode 
  }).format(val ?? 0);
  
  // Replace non-breaking spaces and narrow non-breaking spaces with regular spaces
  // jsPDF renders \u202f and \u00a0 as '/' — sanitize them
  return formatted
    .replace(/\u202f/g, ' ')   // narrow no-break space → regular space
    .replace(/\u00a0/g, ' ');  // no-break space → regular space
};

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  // ── Colors ──
  const indigo = [79, 70, 229];
  const gray100 = [243, 244, 246];
  const gray400 = [156, 163, 175];
  const gray700 = [55, 65, 81];
  const green = [22, 163, 74];
  const red = [220, 38, 38];
  const white = [255, 255, 255];

  let y = margin;

  // ── Helper: filled rect ──
  const rect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, 'F');
  };

  // ── Helper: text ──
  const text = (str, x, ty, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || gray700));
    doc.text(String(str ?? '—'), x, ty, { align: opts.align || 'left' });
  };

  // ── Helper: horizontal line ──
  const line = (ly, color = gray100) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, ly, W - margin, ly);
  };

  // ════════════════════════════
  // HEADER BAND
  // ════════════════════════════
  rect(0, 0, W, 32, indigo);

  // Brand
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('ClearMotion', margin, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254); // indigo-200
  doc.text('Laboratoire dentaire orthodontique', margin, 20);

  // Invoice label right
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('FACTURE', W - margin, 13, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(invoice.invoice_number, W - margin, 20, { align: 'right' });

  // Status badge inside header
  const statusLabel = invoice.status === 'paid' ? 'PAYÉE' : invoice.status === 'pending' ? 'ENVOYÉE' : 'ENVOYÉE';
  const statusColor = invoice.status === 'paid' ? [220, 252, 231] : invoice.status === 'pending' ? [254, 249, 195] : [219, 234, 254];
  const statusText = invoice.status === 'paid' ? [22, 101, 52] : invoice.status === 'pending' ? [133, 77, 14] : [30, 64, 175];
  doc.setFillColor(...statusColor);
  doc.roundedRect(W - margin - 28, 23, 28, 6.5, 2, 2, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...statusText);
  doc.text(statusLabel, W - margin - 14, 27.5, { align: 'center' });

  y = 40;

  // ════════════════════════════
  // DATES ROW
  // ════════════════════════════
  rect(margin, y, contentW, 14, gray100);

  const col = contentW / 3;
  const dateFields = [
    { label: "Date d'émission", val: fmtDate(invoice.issue_date) },
    { label: "Date d'échéance", val: fmtDate(invoice.due_date) },
    { label: 'Date de paiement', val: fmtDate(invoice.payment_date) },
  ];
  dateFields.forEach((f, i) => {
    const cx = margin + i * col + col / 2;
    text(f.label, cx, y + 5, { size: 7.5, color: gray400, align: 'center' });
    text(f.val, cx, y + 11, { size: 8.5, bold: true, color: gray700, align: 'center' });
  });

  y += 20;

  // ════════════════════════════
  // PARTIES
  // ════════════════════════════
  const halfW = (contentW - 6) / 2;

  // Client box
  rect(margin, y, halfW, 26, gray100);
  text('FACTURÉ À', margin + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  text(caseItem.dentist?.full_name || caseItem.patient?.name || '—', margin + 4, y + 13, { size: 9, bold: true });
  text(caseItem.dentist?.email || '—', margin + 4, y + 19, { size: 8, color: gray400 });

  // Case box
  const cx2 = margin + halfW + 6;
  rect(cx2, y, halfW, 26, gray100);
  text('INFORMATIONS DU CAS', cx2 + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  text(`Cas : ${caseItem.case_number}`, cx2 + 4, y + 13, { size: 9, bold: true });
  text(`Patient : ${caseItem.patient?.name || '—'}`, cx2 + 4, y + 19, { size: 8, color: gray400 });

  y += 33;

  // ════════════════════════════
  // TABLE HEADER
  // ════════════════════════════
  rect(margin, y, contentW, 9, indigo);
  const cols = [
    { label: 'DESCRIPTION', x: margin + 3, w: 80, align: 'left' },
    { label: 'ALIGNEURS', x: margin + 85, w: 25, align: 'center' },
    { label: 'PLAGE', x: margin + 112, w: 30, align: 'center' },
    { label: 'MONTANT HT', x: W - margin - 3, w: 30, align: 'right' },
  ];
  cols.forEach((c) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    doc.text(c.label, c.x, y + 6, { align: c.align });
  });
  y += 9;

  // TABLE ROW
  rect(margin, y, contentW, 18, [249, 250, 251]);
  line(y, [229, 231, 235]);

  const phaseName = 'LOT ' + (phase?.phase_number || '');
  const phaseDesc = `Cas ${caseItem.case_number} — ${caseItem.patient?.name || ''}`;
  text(phaseName, margin + 3, y + 7, { size: 9, bold: true });
  text(phaseDesc, margin + 3, y + 13, { size: 7.5, color: gray400 });

  text(String(phase?.aligners_produced_at_billing ?? '—'), margin + 85 + 12.5, y + 10, { size: 9, align: 'center' });

  const rangeLabel = phase?.billing_phase
    ? `${phase.billing_phase.aligner_range_from}–${phase.billing_phase.aligner_range_to}`
    : '—';
  text(rangeLabel, margin + 112 + 15, y + 10, { size: 9, align: 'center' });

  text(fmt(invoice.subtotal), W - margin - 3, y + 10, { size: 9, bold: true, align: 'right' });

  line(y + 18, [229, 231, 235]);
  y += 26;

  // ════════════════════════════
  // TOTALS
  // ════════════════════════════
  const totalsX = W - margin - 60;
  const totalsLabelX = totalsX + 2;
  const totalsValX = W - margin - 2;

  const totalRows = [
    { label: 'Sous-total', val: fmt(invoice.subtotal), color: gray700 },
    ...(invoice.discount_amount > 0
      ? [{ label: 'Remise', val: `-${fmt(invoice.discount_amount)}`, color: green }]
      : []),
    ...(invoice.tax_amount > 0
      ? [{ label: `TVA (${invoice.tax_percentage ?? 0}%)`, val: fmt(invoice.tax_amount), color: gray700 }]
      : []),
  ];

  totalRows.forEach((row) => {
    text(row.label, totalsLabelX, y + 5, { size: 8, color: gray400 });
    text(row.val, totalsValX, y + 5, { size: 8, bold: true, color: row.color, align: 'right' });
    line(y + 7);
    y += 9;
  });

  // Total final
  rect(totalsX, y, 60, 12, indigo);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('TOTAL', totalsLabelX + 2, y + 8);
  doc.text(fmt(invoice.total_amount), totalsValX - 2, y + 8, { align: 'right' });
  y += 17;

  // Paid / Balance
  text('Montant payé', totalsLabelX, y + 5, { size: 8.5, color: gray400 });
  text(fmt(invoice.amount_paid), totalsValX, y + 5, { size: 8.5, bold: true, color: green, align: 'right' });
  line(y + 7);
  y += 9;

  const balanceColor = (invoice.balance_due ?? 0) > 0 ? red : green;
  text('Solde dû', totalsLabelX, y + 5, { size: 8.5, bold: true, color: gray700 });
  text(fmt(invoice.balance_due), totalsValX, y + 5, { size: 8, bold: true, color: balanceColor, align: 'right' });
  y += 14;

  // ════════════════════════════
  // PAYMENT METHOD
  // ════════════════════════════
  if (invoice.payment_method) {
    rect(margin, y, contentW, 10, gray100);
    text('Méthode de paiement :', margin + 4, y + 7, { size: 8, color: gray400 });
    text(invoice.payment_method, margin + 50, y + 7, { size: 8, bold: true });
    y += 15;
  }

  // ════════════════════════════
  // NOTES
  // ════════════════════════════
  if (invoice.notes) {
    text('Notes :', margin, y + 5, { size: 8, bold: true, color: gray400 });
    y += 8;
    const noteLines = doc.splitTextToSize(invoice.notes, contentW);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray700);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 5 + 6;
  }

  // ════════════════════════════
  // FOOTER BAND
  // ════════════════════════════
  const footerY = 285;
  rect(0, footerY, W, 12, indigo);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(`Générée le ${new Date().toLocaleDateString('fr-FR')}`, margin, footerY + 7);
  doc.text(invoice.invoice_number, W / 2, footerY + 7, { align: 'center' });
  doc.text('ClearMotion', W - margin, footerY + 7, { align: 'right' });

  // ── Save ──
  doc.save(`Facture_${invoice.invoice_number}.pdf`);
};
  const getCurrentUser = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      setCurrentUser(user);

      const { data: roleData } = await supabase
        .from('user_role_assignments')
        .select('user_roles(role_type)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (roleData?.user_roles?.role_type === 'administrator') {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchDentists = async () => {
    try {
      const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, email') // ← removed commercial_packages join
      .order('full_name');


      if (error) throw error;

      const dentistsList = [];
      for (const profile of data || []) {
        const { data: roleData } = await supabase
          .from('user_role_assignments')
          .select('user_roles(role_type)')
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .single();

        if (roleData?.user_roles?.role_type === 'dentist_client') {
          dentistsList.push(profile);
        }
      }

      setDentists(dentistsList);
      if (dentistsList.length > 0) {
        setSelectedDentist(dentistsList[0].id);
      }
    } catch (error) {
      console.error('Error fetching dentists:', error);
    }
  };

const fetchCasesData = async (dentistId) => {
  try {
    setLoading(true);

    const { data: cases, error: casesError } = await supabase
      .from('cases')
      .select(`
        id,
        case_number,
        status,
        created_date,
        package,
        patient:patients(
          id,
          name,
          patient_code
        ),
        casePackage:commercial_packages!cases_package_fkey(
          id,
          package_name,
          tier
        ),
        dentist:user_profiles!cases_user_profile_id_fkey(
          id,
          full_name,
          email,
          country:countries(
            id,
            country_name,
            currency_code
          )
        )
      `)
      .eq('user_profile_id', dentistId)
      .order('created_date', { ascending: false });

    if (casesError) throw casesError;

    const casesWithDetails = await Promise.all(
      (cases || []).map(async (caseItem) => {
        const { data: phaseInvoices } = await supabase
          .from('case_phase_invoices')
          .select(`
            id,
            phase_number,
            phase_name,
            aligners_produced_at_billing,
            phase_price,
            status,
            billing_date,
            production_batch_id,
            notes,
            invoice:invoices(
              id,
              invoice_number,
              issue_date,
              due_date,
              payment_date,
              subtotal,
              total_amount,
              amount_paid,
              balance_due,
              discount_amount,
              status,
              notes,
              payment_method,
              client_id,
              country_id
            ),
            billing_phase:package_billing_phases(
              aligner_range_from,
              aligner_range_to
            )
          `)
          .eq('case_id', caseItem.id)
          .order('phase_number');

        const { data: batches } = await supabase
          .from('production_batches')
          .select(`
            id,
            batch_number,
            status,
            start_date,
            completion_date,
            model_up_from,
            model_up_to,
            model_low_from,
            model_low_to,
            current_phase,
            notes
          `)
          .eq('case_id', caseItem.id)
          .order('start_date', { ascending: false });

        // ── Total aligners: sum ALL batches of this case ──
        const totalAligners = (batches || []).reduce((sum, b) => {
          const up  = (b.model_up_to  != null && b.model_up_from  != null && b.model_up_to  >= b.model_up_from)
            ? b.model_up_to  - b.model_up_from  : 0;
          const low = (b.model_low_to != null && b.model_low_from != null && b.model_low_to >= b.model_low_from)
            ? b.model_low_to - b.model_low_from : 0;
          return sum + up + low;
        }, 0);

        const totalInvoiced = (phaseInvoices || []).reduce(
          (sum, p) => sum + parseFloat(p.phase_price || 0), 0
        );
        const totalPaid = (phaseInvoices || [])
          .filter(p => p.invoice)
          .reduce((sum, p) => sum + parseFloat(p.invoice.amount_paid || 0), 0);
        const totalPending = totalInvoiced - totalPaid;

        return {
          ...caseItem,
          phaseInvoices: phaseInvoices || [],
          batches: batches || [],
          totalAligners,
          totalInvoiced,
          totalPaid,
          totalPending,
          currencyCode: caseItem.dentist?.country?.currency_code || 'EUR',
          // ── Package now comes from the case, not the user ──
          packageName: caseItem.casePackage?.package_name || null,
          packageTier: caseItem.casePackage?.tier || null,
        };
      })
    );

    setCasesData(casesWithDetails);
    setFilteredCases(casesWithDetails);
  } catch (error) {
    console.error('Error fetching cases data:', error);
    toast.error('Erreur lors du chargement des données');
  } finally {
    setLoading(false);
  }
};

  const handleOpenDiscountModal = async (invoice, caseItem) => {
    try {
      setSelectedInvoiceForDiscount(invoice);
      setSelectedCaseForDiscount(caseItem);
      setShowDiscountModal(true);

      toast.info('Chargement des remises disponibles...');

      const invoiceData = {
        client_id: invoice.client_id,
        country_id: caseItem.dentist?.country?.id || invoice.country_id,
        subtotal: parseFloat(invoice.subtotal || invoice.total_amount),
        case_id: caseItem.id
      };

      const rules = await discountService.getAvailableRulesForDisplay(invoiceData);
      setAvailableDiscountRules(rules);

      if (rules.length === 0) {
        toast.info('Aucune remise disponible pour cette facture');
      }
    } catch (error) {
      console.error('Error loading discount rules:', error);
      toast.error('Erreur lors du chargement des remises');
      setShowDiscountModal(false);
    }
  };

  const handleApplySelectedDiscount = async (ruleId) => {
    try {
      setApplyingDiscount(prev => ({ ...prev, [selectedInvoiceForDiscount.id]: true }));

      const selectedRule = availableDiscountRules.find(r => r.id === ruleId);
      if (!selectedRule) {
        throw new Error('Règle de remise introuvable');
      }

      const invoiceData = {
        subtotal: parseFloat(selectedInvoiceForDiscount.subtotal || selectedInvoiceForDiscount.total_amount)
      };

      await discountService.applySingleDiscount(
        selectedInvoiceForDiscount.id,
        ruleId,
        invoiceData
      );

      const discountAmount = (invoiceData.subtotal * parseFloat(selectedRule.discount_percentage)) / 100;

      toast.success(`Remise de ${discountAmount.toFixed(2)}€ appliquée !`, {
        description: `${selectedRule.rule_name} (${selectedRule.discount_percentage}%)`,
        duration: 5000
      });

      setShowDiscountModal(false);

      if (isAdmin && selectedDentist) {
        await fetchCasesData(selectedDentist);
      } else if (currentUser) {
        await fetchCasesData(currentUser.id);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      toast.error('Erreur lors de l\'application de la remise', {
        description: error.message
      });
    } finally {
      setApplyingDiscount(prev => ({ ...prev, [selectedInvoiceForDiscount.id]: false }));
    }
  };

  const filterCases = () => {
    let filtered = [...casesData];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.case_number?.toLowerCase().includes(searchLower) ||
          c.patient?.name?.toLowerCase().includes(searchLower) ||
          c.patient?.patient_code?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter((c) => c.status === filters.status);
    }

    if (filters.phaseStatus !== 'all') {
      filtered = filtered.filter((c) =>
        c.phaseInvoices.some((p) => p.status === filters.phaseStatus)
      );
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(
        (c) => new Date(c.created_date) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(
        (c) => new Date(c.created_date) <= new Date(filters.dateTo)
      );
    }

    setFilteredCases(filtered);
  };

  const handleExport = () => {
    try {
      const exportData = filteredCases.flatMap((caseItem) => {
        if (caseItem.phaseInvoices.length > 0) {
          return caseItem.phaseInvoices.map((phase) => ({
            'N° Cas': caseItem.case_number,
            'Patient': caseItem.patient?.name || '',
            'Code Patient': caseItem.patient?.patient_code || '',
            'Statut Cas': getCaseStatusConfig(caseItem.status).label,
            'Date Création': formatDate(caseItem.created_date),
            'Phase N°': phase.phase_number,
            'Phase Nom': phase.phase_name,
            'Aligneurs Produits': phase.aligners_produced_at_billing || 0,
            'Prix Phase': phase.phase_price || 0,
            'Statut Phase': getPhaseStatusConfig(phase.status).label,
            'Date Facturation': formatDate(phase.billing_date),
            'N° Facture': phase.invoice?.invoice_number || 'Non générée',
            'Date Émission': formatDate(phase.invoice?.issue_date),
            'Date Échéance': formatDate(phase.invoice?.due_date),
            'Montant Total': phase.invoice?.total_amount || 0,
            'Remise': phase.invoice?.discount_amount || 0,
            'Montant Payé': phase.invoice?.amount_paid || 0,
            'Solde Dû': phase.invoice?.balance_due || 0,
            'Statut Facture': phase.invoice?.status || 'N/A',
            'Méthode Paiement': phase.invoice?.payment_method || '',
            'Nombre Batches': caseItem.batches.length,
            'Aligneurs Totaux': caseItem.totalAligners,
            'Total Facturé': caseItem.totalInvoiced,
            'Total Payé': caseItem.totalPaid,
            'Total En Attente': caseItem.totalPending,
            'Devise': caseItem.currencyCode,
          }));
        } else {
          return [{
            'N° Cas': caseItem.case_number,
            'Patient': caseItem.patient?.name || '',
            'Code Patient': caseItem.patient?.patient_code || '',
            'Statut Cas': getCaseStatusConfig(caseItem.status).label,
            'Date Création': formatDate(caseItem.created_date),
            'Phase N°': '',
            'Phase Nom': 'Aucune phase facturée',
            'Aligneurs Produits': 0,
            'Prix Phase': 0,
            'Statut Phase': '',
            'Date Facturation': '',
            'N° Facture': '',
            'Date Émission': '',
            'Date Échéance': '',
            'Montant Total': 0,
            'Remise': 0,
            'Montant Payé': 0,
            'Solde Dû': 0,
            'Statut Facture': '',
            'Méthode Paiement': '',
            'Nombre Batches': caseItem.batches.length,
            'Aligneurs Totaux': caseItem.totalAligners,
            'Total Facturé': caseItem.totalInvoiced,
            'Total Payé': caseItem.totalPaid,
            'Total En Attente': caseItem.totalPending,
            'Devise': caseItem.currencyCode,
          }];
        }
      });

      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header];
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      const dentistName = isAdmin && selectedDentist 
        ? dentists.find(d => d.id === selectedDentist)?.full_name.replace(/\s+/g, '_') 
        : 'dentiste';
      const filename = `factures_${dentistName}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${exportData.length} ligne(s) exportée(s)`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erreur lors de l\'export des données');
    }
  };

  const handleGenerateInvoiceForPhase = async (caseItem, phaseInvoiceId) => {
    try {
      const invoice = await phaseBillingService.generateInvoiceForPhase(phaseInvoiceId);
      toast.success('Facture générée avec succès !', {
        description: `Facture ${invoice.invoice_number} créée`
      });
      
      if (isAdmin && selectedDentist) {
        await fetchCasesData(selectedDentist);
      } else if (currentUser) {
        await fetchCasesData(currentUser.id);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Erreur lors de la génération de la facture', {
        description: error.message
      });
    }
  };

  const handleCheckAndGenerateInvoiceForBatch = async (batch, caseItem) => {
    if (!batch.current_phase) {
      toast.error('Ce batch n\'a pas de phase assignée');
      return;
    }

    if (batch.status !== 'completed') {
      toast.error('Le batch doit être complété pour générer une facture');
      return;
    }

    try {
      const billingResult = await phaseBillingService.checkAndTriggerBillingForBatch(batch.id);

      if (billingResult.success && billingResult.shouldGenerateInvoice) {
        await handleGenerateInvoiceForPhase(caseItem, billingResult.phaseInvoiceRecord.id);
      } else if (billingResult.success && !billingResult.shouldGenerateInvoice) {
        toast.info(billingResult.message);
      } else {
        toast.error(billingResult.message);
      }
    } catch (error) {
      console.error('Error checking billing for batch:', error);
      toast.error('Erreur lors de la vérification de la facturation');
    }
  };

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  const formatCurrency = (value, currencyCode = 'EUR') => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode || 'EUR',
      }).format(value ?? 0);
    } catch {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(value ?? 0);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCaseStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Brouillon', color: 'bg-gray-500', icon: 'FileText' },
      submitted: { label: 'Soumis', color: 'bg-blue-500', icon: 'Send' },
      in_progress: { label: 'En cours', color: 'bg-yellow-500', icon: 'Clock' },
      completed: { label: 'Terminé', color: 'bg-green-500', icon: 'CheckCircle' },
      cancelled: { label: 'Annulé', color: 'bg-red-500', icon: 'XCircle' },
      on_hold: { label: 'En attente', color: 'bg-orange-500', icon: 'Pause' }
    };
    return configs[status] || configs.draft;
  };

  const getPhaseStatusConfig = (status) => {
    const configs = {
      pending: {
        label: 'En attente',
        className: 'bg-warning/10 text-warning border-warning/20',
        icon: 'Clock'
      },
      invoiced: {
        label: 'Facturée',
        className: 'bg-primary/10 text-primary border-primary/20',
        icon: 'FileText'
      },
      paid: {
        label: 'Payée',
        className: 'bg-success/10 text-success border-success/20',
        icon: 'CheckCircle'
      },
      cancelled: {
        label: 'Annulée',
        className: 'bg-destructive/10 text-destructive border-destructive/20',
        icon: 'XCircle'
      }
    };
    return configs[status] || configs.pending;
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'bg-amber-700 text-white',
      silver: 'bg-gray-400 text-gray-900',
      gold: 'bg-yellow-500 text-gray-900',
      platinum: 'bg-purple-600 text-white'
    };
    return colors[tier] || 'bg-gray-500 text-white';
  };

  const calculateTotalStats = () => {
    const totalCases = filteredCases.length;
    const totalInvoiced = filteredCases.reduce((sum, c) => sum + c.totalInvoiced, 0);
    const totalPaid = filteredCases.reduce((sum, c) => sum + c.totalPaid, 0);
    const totalPending = filteredCases.reduce((sum, c) => sum + c.totalPending, 0);
    const totalPhases = filteredCases.reduce((sum, c) => sum + c.phaseInvoices.length, 0);

    return { totalCases, totalInvoiced, totalPaid, totalPending, totalPhases };
  };

  const stats = calculateTotalStats();
  const currency = filteredCases[0]?.currencyCode || 'EUR';

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Suivi détaillé des factures
              </h1>
              <p className="text-muted-foreground">
                Vue complète : Cas → Phases → Batches → Factures
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => fetchCasesData(isAdmin ? selectedDentist : currentUser?.id)}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={loading}
              >
                Actualiser
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExport}
              >
                Exporter
              </Button>
            </div>
          </div>

          {/* Dentist selector for admin */}
          {isAdmin && (
            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Sélectionner un dentiste
              </label>
              <select
                value={selectedDentist || ''}
                onChange={(e) => setSelectedDentist(e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {dentists.map((dentist) => (
                  <option key={dentist.id} value={dentist.id}>
                    {dentist.full_name} — {dentist.email}
                 
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Briefcase" size={16} className="text-primary" />
                <p className="text-xs text-muted-foreground">Cas totaux</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.totalCases}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Layers" size={16} className="text-primary" />
                <p className="text-xs text-muted-foreground">Phases facturées</p>
              </div>
              <p className="text-2xl font-bold text-foreground">{stats.totalPhases}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-primary" />
                <p className="text-xs text-muted-foreground">Total facturé</p>
              </div>
              <p className="text-xl font-bold text-primary">
                {formatCurrency(stats.totalInvoiced, currency)}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <p className="text-xs text-muted-foreground">Total payé</p>
              </div>
              <p className="text-xl font-bold text-success">
                {formatCurrency(stats.totalPaid, currency)}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="AlertCircle" size={16} className="text-warning" />
                <p className="text-xs text-muted-foreground">En attente</p>
              </div>
              <p className="text-xl font-bold text-warning">
                {formatCurrency(stats.totalPending, currency)}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Rechercher
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="N° cas, patient..."
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Statut du cas
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="draft">Brouillon</option>
                  <option value="submitted">Soumis</option>
                  <option value="in_progress">En cours</option>
                  <option value="completed">Terminé</option>
                  <option value="cancelled">Annulé</option>
                  <option value="on_hold">En attente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Statut phase
                </label>
                <select
                  value={filters.phaseStatus}
                  onChange={(e) => setFilters({ ...filters, phaseStatus: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toutes les phases</option>
                  <option value="pending">En attente</option>
                  <option value="invoiced">Facturée</option>
                  <option value="paid">Payée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Date début
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Date fin
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFilters({
                    search: '',
                    status: 'all',
                    phaseStatus: 'all',
                    dateFrom: '',
                    dateTo: ''
                  })
                }
                iconName="X"
                iconPosition="left"
              >
                Réinitialiser
              </Button>
              <p className="text-sm text-muted-foreground ml-auto">
                {filteredCases.length} cas affichés
              </p>
            </div>
          </div>

          {/* Cases List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement des données...</span>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Aucun cas trouvé
              </h3>
              <p className="text-muted-foreground">
                Aucun cas ne correspond à vos critères de recherche
              </p>
            </div>
          ) : (
            <>
              {/* All Invoices Summary Section */}
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Receipt" size={20} className="text-primary" />
                  Toutes les factures générées ({
                    filteredCases.reduce((sum, c) => 
                      sum + c.phaseInvoices.filter(p => p.invoice).length, 0
                    )
                  })
                </h2>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/30 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">
                          N° Facture
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">
                          Cas / Patient
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">
                          Phase
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-foreground">
                          Aligneurs
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">
                          Montant
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">
                          Remise
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">
                          Date émission
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">
                          Détails
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-foreground">
                          Statut
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredCases.flatMap((caseItem) =>
                        caseItem.phaseInvoices
                          .filter(p => p.invoice)
                          .map((phase) => {
                            const invoice = phase.invoice;
                            const isOverdue = invoice.due_date && 
                              invoice.due_date < new Date().toISOString().split('T')[0] &&
                              invoice.status !== 'paid';

                            return (
                              <tr key={invoice.id} className="hover:bg-muted/20 transition-colors">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-foreground">
                                    {invoice.invoice_number}
                                  </p>
                                </td>
                                <td className="px-4 py-3">
                                  <p className="text-sm font-medium text-foreground">
                                    {caseItem.case_number}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {caseItem.patient?.name}
                                  </p>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                      <span className="text-xs font-bold text-primary">
                                        {phase.phase_number}
                                      </span>
                                    </div>
                                    <span className="text-sm text-foreground">
                                      {phase.phase_name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="text-sm font-medium text-foreground">
                                    {phase.aligners_produced_at_billing}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {invoice.discount_amount > 0 && (
                                    <p className="text-xs text-muted-foreground line-through">
                                      {formatCurrency(invoice.subtotal, caseItem.currencyCode)}
                                    </p>
                                  )}
                                  <p className="text-sm font-bold text-primary">
                                    {formatCurrency(invoice.total_amount, caseItem.currencyCode)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Payé: {formatCurrency(invoice.amount_paid, caseItem.currencyCode)}
                                  </p>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {invoice.discount_amount > 0 ? (
                                    <div className="flex flex-col items-end">
                                      <span className="text-sm font-semibold text-success flex items-center gap-1">
                                        <Icon name="Tag" size={14} />
                                        -{formatCurrency(invoice.discount_amount, caseItem.currencyCode)}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        Remise appliquée
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm text-foreground">
                                    {formatDate(invoice.issue_date)}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-sm ${isOverdue ? 'text-destructive font-semibold' : 'text-foreground'}`}>
                                    {invoice.notes}
                                  </span>
                                  {isOverdue && (
                                    <p className="text-xs text-destructive flex items-center gap-1 mt-1">
                                      <Icon name="AlertCircle" size={12} />
                                      En retard
                                    </p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                      invoice.status === 'paid'
                                        ? 'bg-success/10 text-success'
                                        : invoice.status === 'pending'
                                        ? 'bg-warning/10 text-warning'
                                        : 'bg-primary/10 text-primary'
                                    }`}
                                  >
                                    {invoice.status === 'paid' ? 'Payée' : 
                                     invoice.status === 'pending' ? 'Envoyée' : 'Envoyée'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    {!invoice.discount_amount && invoice.status !== 'paid' && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        iconName="Tag"
                                        iconPosition="left"
                                        onClick={() => handleOpenDiscountModal(invoice, caseItem)}
                                        disabled={applyingDiscount[invoice.id]}
                                      >
                                        Remise
                                      </Button>
                                    )}
<Button
  variant="outline"
  size="sm"
  iconName="Download"
  iconPosition="left"
  onClick={() => handleDownloadInvoicePdf(phase.invoice, caseItem, phase)}
>
  PDF
</Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredCases.reduce((sum, c) => 
                  sum + c.phaseInvoices.filter(p => p.invoice).length, 0
                ) === 0 && (
                  <div className="text-center py-8">
                    <Icon name="FileText" size={40} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Aucune facture générée pour le moment</p>
                  </div>
                )}
              </div>

              {/* Cases List with Details */}
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Briefcase" size={20} className="text-primary" />
                  Détails par cas ({filteredCases.length})
                </h2>

                <div className="space-y-4">
                  {filteredCases.map((caseItem) => {
                    const caseStatusConfig = getCaseStatusConfig(caseItem.status);
const packageTier = caseItem.packageTier;

                    return (
                      <div
                        key={caseItem.id}
                        className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        {/* Case Header */}
                        <div className="bg-muted/30 border-b border-border p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-bold text-foreground">
                                  {caseItem.case_number} 
   
                                </h3>
                                                               {caseItem.packageName && (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${getTierColor(packageTier)}`}>
    {caseItem.packageName}
  </span>
)}
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${caseStatusConfig.color} text-white`}
                                >
                                  {caseStatusConfig.label}
                                </span>
                                {packageTier && (
                                  <span
                                    className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${getTierColor(
                                      packageTier
                                    )}`}
                                  >
                                   
                                  </span>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Patient</p>
                                  <p className="font-medium text-foreground">
                                    {caseItem.patient?.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {caseItem.patient?.patient_code}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-muted-foreground">Date création</p>
                                  <p className="font-medium text-foreground">
                                    {formatDate(caseItem.created_date)}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-muted-foreground">Aligneurs totaux</p>
                                  <p className="font-medium text-foreground">
                                    {caseItem.totalAligners}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-muted-foreground">Phases facturées</p>
                                  <p className="font-medium text-foreground">
                                    {caseItem.phaseInvoices.length}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                setSelectedCase(caseItem);
                                setShowDetailsModal(true);
                              }}
                              className="ml-4 p-2 rounded-lg hover:bg-muted transition-colors"
                              title="Voir les détails"
                            >
                              <Icon name="ChevronRight" size={20} />
                            </button>
                          </div>

                          {/* Financial Summary */}
                          <div className="grid grid-cols-3 gap-3 mt-4">
                            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1">Total facturé</p>
                              <p className="text-lg font-bold text-primary">
                                {formatCurrency(caseItem.totalInvoiced, caseItem.currencyCode)}
                              </p>
                            </div>

                            <div className="bg-success/5 border border-success/20 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1">Total payé</p>
                              <p className="text-lg font-bold text-success">
                                {formatCurrency(caseItem.totalPaid, caseItem.currencyCode)}
                              </p>
                            </div>

                            <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground mb-1">En attente</p>
                              <p className="text-lg font-bold text-warning">
                                {formatCurrency(caseItem.totalPending, caseItem.currencyCode)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Phases List */}
                        {caseItem.phaseInvoices.length > 0 && (
                          <div className="p-4">
                            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                              <Icon name="Layers" size={16} className="text-primary" />
                              Phases de facturation ({caseItem.phaseInvoices.length})
                            </h4>

                            <div className="space-y-2">
                              {caseItem.phaseInvoices.map((phase) => {
                                const phaseStatusConfig = getPhaseStatusConfig(phase.status);

                                return (
                                  <div
                                    key={phase.id}
                                    className="bg-muted/20 border border-border rounded-lg p-3"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                          <span className="text-sm font-bold text-primary">
                                            {phase.phase_number}
                                          </span>
                                        </div>
                                        <div>
                                          <p className="font-semibold text-foreground text-sm">
                                            {phase.phase_name} (LOT {
    caseItem.batches.find(b => b.id === phase.production_batch_id)?.current_phase ?? '—'
  })
                                          </p>
                                          <p className="text-xs text-muted-foreground">
                                            {phase.aligners_produced_at_billing} aligneurs
                                            {phase.billing_phase &&
                                              ` (${phase.billing_phase.aligner_range_from}-${phase.billing_phase.aligner_range_to})`}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-right">
                                        <p className="text-sm font-bold text-primary mb-1">
                                          {formatCurrency(phase.phase_price, caseItem.currencyCode)}
                                        </p>
                                        <span
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${phaseStatusConfig.className}`}
                                        >
                                          <Icon name={phaseStatusConfig.icon} size={11} />
                                          {phaseStatusConfig.label}
                                        </span>
                                      </div>
                                    </div>

                                    {phase.billing_date && (
                                      <p className="text-xs text-muted-foreground mb-2">
                                        Facturé le {formatDate(phase.billing_date)}
                                      </p>
                                    )}

                                    {phase.invoice && (
                                      <div className="bg-background border border-border rounded p-2 mt-2">
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <p className="text-xs font-medium text-foreground">
                                              Facture: {phase.invoice.invoice_number}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                              Émise: {formatDate(phase.invoice.issue_date)}
                                            </p>
                                          </div>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            iconName="Eye"
                                            iconPosition="left"
                                            onClick={() =>
                                              window.open(
                                                `/billing-overview?invoice=${phase.invoice.id}`,
                                                '_blank'
                                              )
                                            }
                                          >
                                            Voir
                                          </Button>
                                        </div>
                                      </div>
                                    )}

                                    {phase.status === 'pending' && !phase.invoice_id && (
                                      <div className="mt-3">
                                        <Button
                                          variant="default"
                                          size="sm"
                                          fullWidth
                                          iconName="FileText"
                                          iconPosition="left"
                                          onClick={() => handleGenerateInvoiceForPhase(caseItem, phase.id)}
                                        >
                                          Générer la facture
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Batches Summary */}
                        {caseItem.batches.length > 0 && (
                          <div className="border-t border-border p-4 bg-muted/10">
                            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                              <Icon name="Package" size={16} className="text-primary" />
                              Batches de production ({caseItem.batches.length})
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              {caseItem.batches.slice(0, 20).map((batch) => {
                                const batchAligners = 
                                  ((batch.model_up_to - batch.model_up_from) || 0) +
                                  ((batch.model_low_to - batch.model_low_from) || 0);
                                
                                const isCompleted = batch.status === 'completed';
                                const hasPhase = batch.current_phase;
                                
const phaseAlreadyInvoiced = caseItem.phaseInvoices.some(
  p => (p.production_batch_id === batch.id && !!p.invoice)
);

                                return (
                                  <div
                                    key={batch.id}
                                    className="bg-card border border-border rounded-lg p-3"
                                  >
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <p className="text-xs font-medium text-foreground mb-1">
                                        LOT {batch.current_phase} :   {batch.batch_number}
                                        </p>
                                        {hasPhase && (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                            <Icon name="Layers" size={10} />
                                            Phase {batch.current_phase}
                                          </span>
                                        )}
                                      </div>
                                      
                                    </div>

                                    <p className="text-xs text-muted-foreground mb-1">
                                      Sup: {batch.model_up_from}-{batch.model_up_to} | Inf:{' '}
                                      {batch.model_low_from}-{batch.model_low_to}
                                    </p>
                                    <p className="text-xs text-muted-foreground mb-2">
                                      {batchAligners} aligneurs — {formatDate(batch.start_date)}
                                    </p>

                                    {phaseAlreadyInvoiced ? (
  <div className="flex items-center gap-1 text-xs text-success">
    <Icon name="CheckCircle" size={12} />
    <span>Déjà facturé</span>
  </div>
) : (
  <div className="flex items-center gap-1 text-xs text-muted-foreground">
    <Icon name="Clock" size={12} />
    <span>Non facturé</span>
  </div>
)}
                                  </div>
                                );
                              })}
                            </div>

                            
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Details Modal */}
      {showDetailsModal && selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in overflow-y-auto">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-6xl my-8 animate-slide-in">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Détails complets du cas
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedCase.case_number} — {selectedCase.patient?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCase(null);
                }}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Informations du cas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Patient</p>
                    <p className="font-medium text-foreground">{selectedCase.patient?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Statut</p>
                    <p className="font-medium text-foreground">
                      {getCaseStatusConfig(selectedCase.status).label}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Aligneurs totaux</p>
                    <p className="font-medium text-foreground">{selectedCase.totalAligners}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phases facturées</p>
                    <p className="font-medium text-foreground">
                      {selectedCase.phaseInvoices.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Toutes les phases
                </h3>
                <div className="space-y-3">
                  {selectedCase.phaseInvoices.map((phase) => {
                    const phaseStatusConfig = getPhaseStatusConfig(phase.status);
                    return (
                      <div
                        key={phase.id}
                        className="bg-muted/20 border border-border rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-bold text-primary">
                                {phase.phase_number}
                              </span>
                            </div>
                            <div>
                              <h4 className="font-semibold text-foreground">{phase.phase_name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {phase.aligners_produced_at_billing} aligneurs produits
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-primary mb-1">
                              {formatCurrency(phase.phase_price, selectedCase.currencyCode)}
                            </p>
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${phaseStatusConfig.className}`}
                            >
                              <Icon name={phaseStatusConfig.icon} size={12} />
                              {phaseStatusConfig.label}
                            </span>
                          </div>
                        </div>

                        {phase.invoice && (
                          <div className="bg-background border border-border rounded-lg p-3">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">N° facture</p>
                                <p className="font-medium text-foreground">
                                  {phase.invoice.invoice_number}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Date émission</p>
                                <p className="font-medium text-foreground">
                                  {formatDate(phase.invoice.issue_date)}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Échéance</p>
                                <p className="font-medium text-foreground">
                                  {formatDate(phase.invoice.due_date)}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Montant payé</p>
                                <p className="font-medium text-success">
                                  {formatCurrency(
                                    phase.invoice.amount_paid,
                                    selectedCase.currencyCode
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {phase.notes && (
                          <div className="mt-3 p-2 bg-muted/30 rounded text-xs text-muted-foreground">
                            <Icon name="MessageSquare" size={12} className="inline mr-1" />
                            {phase.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Tous les batches de production
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedCase.batches.map((batch) => (
                    <div key={batch.id} className="bg-muted/20 border border-border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-foreground text-sm">
                            {batch.batch_number}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Statut: {batch.status}
                          </p>
                        </div>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          {((batch.model_up_to - batch.model_up_from) || 0) +
                            ((batch.model_low_to - batch.model_low_from) || 0)}{' '}
                          aligneurs
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Supérieur</p>
                          <p className="font-medium text-foreground">
                            {batch.model_up_from} - {batch.model_up_to}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Inférieur</p>
                          <p className="font-medium text-foreground">
                            {batch.model_low_from} - {batch.model_low_to}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Début</p>
                          <p className="font-medium text-foreground">
                            {formatDate(batch.start_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Fin</p>
                          <p className="font-medium text-foreground">
                            {formatDate(batch.completion_date)}
                          </p>
                        </div>
                      </div>

                      {batch.notes && (
                        <p className="mt-2 text-xs text-muted-foreground italic">{batch.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Selection Modal */}
      {showDiscountModal && selectedInvoiceForDiscount && selectedCaseForDiscount && (
        <DiscountSelectionModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          invoice={selectedInvoiceForDiscount}
          caseItem={selectedCaseForDiscount}
          availableRules={availableDiscountRules}
          onApplyDiscount={handleApplySelectedDiscount}
          isApplying={applyingDiscount[selectedInvoiceForDiscount?.id]}
        />
      )}
    </div>
  );
};

export default DentistInvoicesTracking;